import { BasicEmployeeInfo } from "./../model/user";
import {
  createSlice,
  SliceCaseReducers,
  ValidateSliceCaseReducers,
  PayloadAction,
} from "@reduxjs/toolkit";
import { MouseEvent } from "react";
import constants from "./constants";
import {
  GENERIC_STATE_STATUS,
  ASSESSMENT_EMPLOYEE_RELATIONSHIP as RELATIONSHIP,
  RATING_TYPE,
  SCORE,
} from "./enums";
import {
  AssessmentQuestion,
  AssessmentTemplateQuestion,
  AssessmentTemplateCategory,
  AssessmentReportQuestionMatchScore,
  AssessmentReportFreeQuestionMatchScore,
  AssessmentMatch,
} from "../model/assessment";
import { CompanyCategory } from "../model/company";
import { COLORS } from "./helpers/colors";
import axios from "axios";
import { MONTH } from "./enums";
import dayjs, { Dayjs } from "dayjs";

export type GetKeysByType<T, E> = {
  [K in keyof T]: T[K] extends E ? K : never;
}[keyof T];

export type OnlyStrKeys<T> = Omit<
  T,
  Exclude<keyof T, GetKeysByType<T, string>>
>;
export interface GenericState<T> {
  data?: T;
  status: GENERIC_STATE_STATUS;
}

export const createGenericSlice = <
  T,
  Reducers extends SliceCaseReducers<GenericState<T>>
>({
  name = "",
  initialState,
  reducers,
}: {
  name: string;
  initialState: GenericState<T>;
  reducers: ValidateSliceCaseReducers<GenericState<T>, Reducers>;
}) => {
  return createSlice({
    name,
    initialState,
    reducers: {
      start(state) {
        state.status = GENERIC_STATE_STATUS.PENDING;
      },
      success(state: GenericState<T>, action: PayloadAction<T>) {
        state.data = action.payload;
        state.status = GENERIC_STATE_STATUS.FULFILLED;
      },
      error(state) {
        state.status = GENERIC_STATE_STATUS.REJECTED;
      },
      ...reducers,
    },
  });
};

export const isLightColor = (hex: string | null) => {
  if (hex) {
    const color = hex.substring(1);
    const rgb = parseInt(color, 16);
    const red = (rgb >> 16) & 0xff;
    const green = (rgb >> 8) & 0xff;
    const blue = (rgb >> 0) & 0xff;

    const luminescenceValue = 0.2126 * red + 0.7152 * green + 0.0722 * blue;

    return luminescenceValue > 130;
  } else return null;
};

export const getIdentifiedEmployeeObj = (
  match: AssessmentMatch | null,
  employeeType: "target" | "evaluator"
) => {
  if (match) {
    if (employeeType === "target") {
      return {
        name: match.targetEmployee?.name,
        id: match.targetEmployeeId,
        profileImageUrl: match.targetEmployee?.profileImageUrl,
      } as BasicEmployeeInfo;
    } else if (employeeType === "evaluator") {
      return {
        name: match.evaluatorEmployee?.name,
        id: match.evaluatorId,
        profileImageUrl: match.evaluatorEmployee?.profileImageUrl,
      } as BasicEmployeeInfo;
    } else return null;
  } else return null;
};

export const getRandomNumber = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1) + min);

export const groupBy = <T, K extends keyof any>(
  array: T[],
  key: (item: T) => K
) =>
  Object.values(
    array.reduce((groups, item) => {
      (groups[key(item)] ||= []).push(item);
      return groups;
    }, {} as Record<K, T[]>)
  );

export const getCompanyId = () => {
  let id = localStorage.getItem(constants.LOCAL_STORAGE.KEYS.COMPANY_ID);

  if (id && JSON.parse(id)) {
    return parseInt(id);
  } else {
    throw new Error("No company id");
  }
};

export const getActionErrorMessage = (
  err: any,
  fallbackMessage = "An error occurred",
  options?: { getObj: boolean }
) => {
  const { getObj } = options || {};
  return axios.isAxiosError(err)
    ? getObj
      ? err.response?.data
      : err.response?.data.message
    : fallbackMessage;
};

export const getUserNames = (name: string) => {
  if (!name) {
    return { firstName: "", lastName: "" };
  }
  const arr = name.trim().split(" ");
  return {
    firstName: arr[0],
    lastName: arr.slice(1).join(" "),
  };
};

export const getCategorizedQuestions = (
  questions: AssessmentQuestion[] | AssessmentTemplateQuestion[],
  categories_?: CompanyCategory[]
): AssessmentTemplateCategory[] => {
  const categories: AssessmentTemplateCategory[] = [];

  for (let question_ of questions) {
    const question = question_ as AssessmentQuestion;
    const cat = categories.find((c) => c.id === question.categoryId);
    if (cat) {
      cat.items.push(question);
    } else {
      if (categories_) {
        const c = categories_.find((c) => c.id === question.categoryId);
        if (c) {
          categories.push({
            id: c.id || -1,
            name: c.name,
            position: c.position,
            items: [question],
          });
        }
      } else if (question.category) {
        categories.push({
          id: question.categoryId,
          name: question.category.name,
          position: question.category.position,
          items: [question],
        });
      }
    }
  }

  return categories;
};

export const getFilterMonthDetails = (month: number) => {
  const date = new Date();
  const currentMonth = date.getMonth();
  const year =
    month > currentMonth ? date.getFullYear() - 1 : date.getFullYear();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  return {
    firstDay: formatForApi(dayjs(firstDay)),
    lastDay: formatForApi(dayjs(lastDay)),
  };
};

export const getFilterMonthOptions = (
  startDay: string = constants.CF_START_DAY
) => {
  const startDate = dayjs(startDay);
  const startYear = startDate.year();
  const currentDate = dayjs();
  const diff = currentDate.diff(startDate, "months");
  const months = [];

  for (let i = 0; i <= diff; i++) {
    const index = startDate.month() + i;
    months.push({
      text: `${MONTH[index < 12 ? index : index - 12]}, ${
        index < 12 ? startYear : startYear + 1
      }`,
      value: index < 12 ? index : index - 12,
    });
  }
  return months;
};

const relationshipsInfo = [
  {
    name: "Direct Manager",
    type: RELATIONSHIP.MANAGER,
    sentenceName: "manager",
    priority: 4,
  },
  {
    name: "Your view",
    type: RELATIONSHIP.SELF,
    sentenceName: "self feedback",
    priority: 5,
  },
  { name: "Peers", type: RELATIONSHIP.PEER, sentenceName: "peer", priority: 3 },
  {
    name: "Reporters",
    type: RELATIONSHIP.REPORT,
    sentenceName: "report",
    priority: 1,
  },
  {
    name: "Others",
    type: RELATIONSHIP.GENERIC_COLLEAGUE,
    sentenceName: "other colleague",
    priority: 2,
  },
];

export const getRelationshipsFromScores = (
  scores:
    | AssessmentReportQuestionMatchScore[]
    | AssessmentReportFreeQuestionMatchScore[]
) => {
  return scores
    .map((s) => {
      return { ...relationshipsInfo.find((i) => i.type === s.type), score: s };
    })
    .sort((a, b) => (a.priority && b.priority ? b.priority - a.priority : 0));
};

export const getScoreColorScheme = (
  score: number,
  ratingType: RATING_TYPE = RATING_TYPE.one_to_five
) => {
  const s = (score / (ratingType === RATING_TYPE.one_to_five ? 5 : 10)) * 100;

  if (s >= SCORE.EXCELLENT) {
    return {
      foreground: COLORS.greenDark,
      background: COLORS.excellentGreenBg,
    };
  } else if (s >= SCORE.VERY_GOOD && s < SCORE.EXCELLENT) {
    return { foreground: COLORS.greenDark, background: COLORS.veryGoodGreenBg };
  } else if (s >= SCORE.GOOD && s < SCORE.VERY_GOOD) {
    return { foreground: COLORS.goodGreenFg, background: COLORS.goodGreenBg };
  } else {
    return { foreground: COLORS.redDark, background: COLORS.redLight };
  }
};

export const getPopulatedParamsLink = (
  link: string,
  values: { [key: string]: string | number }
) => {
  if (link && values) {
    Object.keys(values).forEach((key) => {
      link = link.replaceAll(`:${key}`, `${values[key]}`);
    });
  }
  return link;
};

export const getPercentage = (score: number, total: number) => {
  return (score / total) * 100;
};

export const stopClickPropagation = (e: MouseEvent<HTMLElement>) => {
  e.stopPropagation();
};

export const sortByPosition = <T>(items: (T & { position: number })[]) => {
  const sortArray = items.slice();
  return sortArray.sort((a, b) =>
    a.position && b.position ? a.position - b.position : 1
  );
};

export const isBetween = (
  value: number,
  min: number,
  max: number,
  include?: "min" | "max" | "both"
) => {
  switch (include) {
    case "min":
      return value >= min && value < max;
    case "max":
      return value > min && value <= max;
    case "both":
      return value >= min && value <= max;
    default:
      return value > min && value < max;
  }
};

export const capitalizeFirstLetter = (str: string) => {
  const trimmedStr = str.trim();
  return trimmedStr.charAt(0).toUpperCase() + trimmedStr.slice(1);
};

export const establishColor = (
  score: number | null,
  ratingType: RATING_TYPE
) => {
  const criteria: number[] = [];
  for (let criterion = 0; criterion <= 5; criterion++) {
    criteria.push(criterion * (ratingType === RATING_TYPE.one_to_ten ? 2 : 1));
  }
  const [lowest, low, mid, high, higher, highest] = criteria;

  if (score || score === 0) {
    if (isBetween(score, lowest, low, "min")) return COLORS.red;
    if (isBetween(score, low, mid, "min")) return COLORS.orange;
    if (isBetween(score, mid, high, "min")) return COLORS.goodGreenBg;
    if (isBetween(score, high, higher, "min")) return COLORS.veryGoodGreenBg;
    if (isBetween(score, higher, highest, "both"))
      return COLORS.excellentGreenBg;
    return COLORS.violetLight;
  }
  return COLORS.violetLight;
};

export const getStringOccurrences = (mainStr: string, targetStr: string) =>
  mainStr.split(targetStr).length - 1;

export const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const formatForApi = (date: Dayjs) =>
  date.format(constants.DEFAULT_API_DATE_FORMAT);
